import { BusinessWindow } from "../../../dashboard-framework/businessMinutes";
import { AggregateQuerySpec, DateFilterKey, GlobalFilterConfig } from "../../../dashboard-framework/types";
import { FIRST_EEG_SERVICE, NFB_SERVICE, PSY_SERVICE } from "../../../utils/constants";

// Fórmulas extraídas do Zoho "Reception KPIs" em 2026-06-11 (ver spec.md):
// is_first_visit = sessões com lead associada e serviço 39 (qEEG Primeira) ou 34 (Psicologia);
// attendance = done/(done+cancelled+missed); minutos de retorno = business_minutes nas janelas
// 10-13h + 14-17h (ou 14-19:15h), sem fins de semana, criação convertida para Europe/Lisbon.

export const RECEPTION_KPIS_KEY = "reception-kpis";

// Réplica do filtro global Zoho "Data" (default Este mês): aplica-se aos cartões de retorno,
// às taxas de comparência e ao gráfico diário — todos por data de CRIAÇÃO.
export const receptionKpisGlobalFilters: Array<GlobalFilterConfig> = [
    { key: "timeline", type: "datePreset", label: "Período", field: "date_created", defaultPreset: "este_mes" },
];

export const FIRST_VISIT_SERVICES = [FIRST_EEG_SERVICE, PSY_SERVICE];

export const FIRST_VISIT_FILTER = {
    _and: [{ lead: { _nnull: true } }, { service: { _in: FIRST_VISIT_SERVICES } }],
};

export const RETURN_WINDOWS_STANDARD: Array<BusinessWindow> = [
    { startMinute: 10 * 60, endMinute: 13 * 60 },
    { startMinute: 14 * 60, endMinute: 17 * 60 },
];

export const RETURN_WINDOWS_FULL_SCHEDULE: Array<BusinessWindow> = [
    { startMinute: 10 * 60, endMinute: 13 * 60 },
    { startMinute: 14 * 60, endMinute: 19 * 60 + 15 },
];

// Filtros do Zoho nos cartões de retorno: leads desde 2025-01-24, canal ≠ chat (mantém NULL),
// exclui as leads outlier 20376/20425 e o feriado 2026-01-01.
export const RETURN_LEADS_SINCE = "2025-01-24";
export const RETURN_EXCLUDED_LEAD_IDS = [20376, 20425];
export const RETURN_LEADS_FILTER = {
    _and: [
        { date_created: { _gte: RETURN_LEADS_SINCE } },
        { _or: [{ channel: { _null: true } }, { channel: { _neq: "chat" } }] },
        { id: { _nin: RETURN_EXCLUDED_LEAD_IDS } },
        { _or: [{ date_created: { _lt: "2026-01-01" } }, { date_created: { _gte: "2026-01-02" } }] },
        { first_contact_attempt: { _nnull: true } },
    ],
};

export const weeklyFirstVisitBookingsQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    fixedFilter: FIRST_VISIT_FILTER,
    dataScope: "global",
};

export const dailyFirstVisitBookingsQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["year(date_created)", "month(date_created)", "day(date_created)"],
    fixedFilter: FIRST_VISIT_FILTER,
    dataScope: "global",
};

export const firstVisitAttendanceQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["status"],
    fixedFilter: FIRST_VISIT_FILTER,
    dataScope: "global",
};

export const sessionsAttendanceQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["status"],
    fixedFilter: { service: { _in: [NFB_SERVICE, PSY_SERVICE] } },
    dataScope: "global",
};

export const monthlyLeadsQuery: AggregateQuerySpec = {
    entity: "leads",
    aggregate: { count: "*" },
    groupBy: ["year(date_created)", "month(date_created)"],
    dataScope: "global",
};

// Leads por estado (leads.status) — como o call center está a gerir a carteira. Sem fixedFilter:
// todos os estados entram, incluindo as leads ainda sem estado. Janela de datas vem do filtro local.
export const leadsByStatusQuery: AggregateQuerySpec = {
    entity: "leads",
    aggregate: { count: "*" },
    groupBy: ["status"],
    dataScope: "global",
};

export const LEADS_STATUS_DATE_PRESETS: Array<DateFilterKey> = [
    "ultimos_7_dias",
    "ultimos_30_dias",
    "ultimos_60_dias",
    "este_mes",
    "ultimo_mes",
    "ultimos_3_meses",
    "ultimos_6_meses",
    "ultimos_12_meses",
];
export const LEADS_STATUS_DEFAULT_DATE_PRESET = "ultimos_60_dias";
export const LEADS_WITHOUT_STATUS_LABEL = "Sem estado";
// Valor sentinela da UI para "lead sem estado" no multiselect (na BD é NULL) — ver leadStatusSelectionFilter.
export const LEADS_WITHOUT_STATUS_VALUE = "__sem_estado";
// Estados "em aberto" — a lead ainda está nas mãos do call center. Seleção default do filtro de estado.
export const OPEN_LEAD_STATUSES = ["thinking", "all_good", "call_planed", "call_1st_time", "for_future", "no_show"];
export const LEADS_STATUS_DEFAULT_SELECTION = [...OPEN_LEAD_STATUSES, LEADS_WITHOUT_STATUS_VALUE];

// Estado "em espera" dos tratamentos (validado exato via SQL Zoho: 2/68/46/24).
export const WAITING_TREATMENT_STATUSES = ["report_done", "thinking", "waiting_report", "waiting_start_of_treatment"];

export const WEEKLY_BOOKINGS_GOAL = 50;

export const DAILY_BOOKINGS_TARGET = 8;

export const CRM_LEAD_LINK_PREFIX = "https://crm.neuroimprove.pt/leads/";

// === Produtividade de receção por pessoa (réplica do Zoho "Dashboard Maria") ===
// Secção com scoping por utilizador: a gestão (viewer_rules com params {}) vê a equipa toda; os
// restantes veem só os próprios números. O dashboard aplica o param `callUserId` (= "$current_user")
// APENAS a estes três widgets — o funil de receção acima continua global para todos os que abrem o
// dashboard. A privacidade é ao nível da vista (o filtro por user_created entra na query, mas não é
// imposto pelas permissões Directus, porque o funil acima precisa de ler todas as sessões/chamadas).
export const RECEPTION_PERSON_VIEWER_PARAM = "callUserId";

// Gestão = vê todos (Diretora técnica + Super admin). Referência para as viewer_rules da row 3
// (o código de runtime lê apenas o param acima; a resolução das regras é do framework).
export const RECEPTION_MANAGER_ROLE_IDS = [
    "d6b0779e-70fc-4854-9525-91e41e555389", // Super admin
    "c3aaca5c-9e37-499e-b061-74690007fe0f", // Diretora técnica (Maria Antunes)
];

// Excluídos da análise de produtividade (não são agentes de receção): a Maria Antunes (Diretora) e o
// Neuro Bot (conta automática). Não têm marcações de 1.ª EEG (0 este mês → total 135 inalterado), mas
// fazem chamadas — por isso são filtrados das tabelas/gráfico por `user_created`.
export const RECEPTION_EXCLUDED_USER_IDS = [
    "a7582027-3906-4f4f-ae19-3b9a890d7053", // Maria Antunes (Diretora técnica)
    "42f3d39e-fff2-438d-aaf6-be21c13e1c93", // Neuro Bot
];

// "1.ª avaliação EEG" do Zoho ("Dashboard Maria") = TODAS as sessões de serviço 39 (qEEG Primeira),
// independentemente de terem lead — NÃO usar is_lead_first_evaluation (que exige lead, e dropava ~11
// sessões/mês de EEGs de clientes existentes, incl. a Filipa Pinho). Validado vs Zoho: 135 marcadas /
// 36 realizadas no mês; Iris 64/13. (Contraste: o funil "1.ª visita" em cima EXIGE lead — é outra métrica.)
export const FIRST_EEG_BOOKING_FILTER = {
    service: { _eq: FIRST_EEG_SERVICE },
};

export const SESSION_DONE_STATUS = "done";

// Marcações de 1.ª EEG por pessoa que as criou (user_created) × estado → marcadas (todas) e
// realizadas (done). % = realizadas ÷ marcadas (fórmula Zoho, não é a taxa de comparência do funil).
export const bookingsByPersonQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["user_created", "status"],
    fixedFilter: FIRST_EEG_BOOKING_FILTER,
    dataScope: "viewer",
};

// Chamadas por operador (= user_created da chamada): nº de chamadas e segundos de conversação
// (call_time_seconds é 0 nas não atendidas, por isso a soma é só tempo de conversa).
export const callsByOperatorQuery: AggregateQuerySpec = {
    entity: "calls",
    aggregate: { count: "*", sum: "call_time_seconds" },
    groupBy: ["user_created"],
    dataScope: "viewer",
};

// Conversação por operador × dia, no período selecionado — base do gráfico (só gestão) de média
// diária por pessoa: cada linha é um dia com chamadas; média = (Σ segundos ÷ 3600) ÷ nº desses dias.
export const callsPeriodByDayQuery: AggregateQuerySpec = {
    entity: "calls",
    aggregate: { sum: "call_time_seconds" },
    groupBy: ["user_created", "year(date_created)", "month(date_created)", "day(date_created)"],
    dataScope: "viewer",
};

// Gráfico "Total de horas de conversação": chamadas em bruto dos últimos N dias, agrupadas
// client-side por dia (hora de Lisboa) × operador (campos mínimos + nome para a legenda).
export const CONVERSATION_HOURS_CHART_DAYS = 7;
export const CALLS_OPERATOR_FIELDS = "id,date_created,call_time_seconds,user_created.id,user_created.first_name,user_created.last_name";

// Filtros locais da "Análise detalhada — minutos para contactar" (independentes do "Período" global):
// janela de criação das leads via DashboardDateFilter partilhado (presets + "Personalizado" De/Até,
// default últimos 30 dias) e limiar de minutos úteis (default > 5 min, réplica do slider "Minutos"
// do Zoho; null no limiar = mostrar todas).
export const DETAIL_DATE_PRESETS: Array<DateFilterKey> = ["ultimos_7_dias", "ultimos_30_dias", "este_mes", "ultimo_mes", "ultimos_3_meses"];
export const DETAIL_DEFAULT_DATE_PRESET = "ultimos_30_dias";
export const DETAIL_MINUTES_THRESHOLD_OPTIONS = [0, 5, 15, 30, 60];
export const DETAIL_DEFAULT_MIN_MINUTES = 5;

// Horário lançado (schedules) por pessoa para o gráfico agendado-vs-chamadas: duração + notas + nome.
export const SCHEDULE_FIELDS = "id,start_date,end_date,notes,user.id,user.first_name,user.last_name";
// Roster da equipa de receção/call center (licenças): define quem aparece com horário mesmo sem chamadas.
export const CALL_TEAM_LICENCE_FIELDS = "user.id,user.first_name,user.last_name";
